import clsx from 'clsx';
import { Badge, Collapse, Dropdown, Empty, Skeleton } from 'antd';
import { RiArrowDownSLine, RiNotification2Line } from 'react-icons/ri';
import { UseNotificationReturn } from './notification.types';
import { NotificationHeader } from './components/notification-header';
import { NotificationCard } from './components/notification-card';
import { NotificationV2 } from '@/types/generated/graphql';
import { notificationTitleDateFormat } from './notification-helper';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

function groupNotificationsByTitleDate(notifications: NotificationV2[]) {
  const groups: { dateTitle: string; notifications: NotificationV2[] }[] = [];
  for (const notification of notifications) {
    const dateTitle = notificationTitleDateFormat(notification?.createdAt);
    const lastGroup = groups[groups.length - 1];
    if (lastGroup?.dateTitle === dateTitle) {
      lastGroup.notifications.push(notification);
    } else {
      groups.push({ dateTitle, notifications: [notification] });
    }
  }
  return groups;
}

export function NotificationView(props: UseNotificationReturn) {
  const { screen } = useScreenSize();
  const { t } = useTranslation();
  const {
    dropdownToggle,
    notifications,
    markAllRead,
    markSingleRead,
    notificationSeen,
    onClickNotification,
    handleMarkedNotification,
  } = props;
  const { isNotificationOpen, closeNotification, openNotification } = dropdownToggle;
  const { data, initialLoading, loadNextRef, hasNextPage } = notifications;
  const { count } = notificationSeen;

  const notificationData = (data?.data ?? []) as NotificationV2[];
  const groupedNotifications = groupNotificationsByTitleDate(notificationData);

  const collapseItems = groupedNotifications.map((group) => {
    const hasUnreadForThisDate = group.notifications.some((n) => !n?.seenAt);
    return {
      key: group.dateTitle,
      label: (
        <span className="leading-5 text-text-700">
          <Badge dot={hasUnreadForThisDate} offset={[-5, 5]} className="pr-0.5!" />
          {group.dateTitle}
        </span>
      ),
      className:
        '[&>.ant-collapse-header]:px-5! [&>.ant-collapse-header]:pt-4.75! [&>.ant-collapse-header]:pb-1.75! [&_.ant-collapse-content-box]:p-0!',
      children: (
        <div>
          {group.notifications.map((notification, index) => (
            <NotificationCard
              key={notification?.id}
              notification={notification}
              markSingleRead={markSingleRead}
              onClickNotification={onClickNotification}
              handleMarkedNotification={handleMarkedNotification}
              isFirstItem={index === 0}
            />
          ))}
        </div>
      ),
    };
  });

  const headerBell = (
    <Badge
      count={count}
      className={clsx(
        'rounded-full p-2! cursor-pointer transition-all duration-300',
        isNotificationOpen ? 'bg-primary-100' : 'hover:bg-gray-200'
      )}
      offset={[-8, 8]}
    >
      <RiNotification2Line className={clsx('size-4.5', isNotificationOpen ? 'text-primary-400' : '')} />
    </Badge>
  );
  return (
    <Dropdown
      // DONOT REMOVE THIS "transitionName",
      // because it overrides default animation
      transitionName=""
      trigger={['click']}
      placement="bottomRight"
      open={isNotificationOpen}
      onOpenChange={(open) => (open ? openNotification() : closeNotification())}
      overlayClassName={clsx(
        screen === 'xs' && 'left-1/2! -translate-x-1/2',
        'absolute sm:!top-[60px] w-[90%] sm:w-[380px]',
        'overflow-hidden bg-surface border border-gray-700 shadow-xl shadow-primary-100 rounded-xl'
      )}
      popupRender={() => (
        <div className="transition-all duration-300 overflow-x-hidden bg-surface!">
          <NotificationHeader markAllRead={markAllRead} showMarkAll={Boolean(notificationData?.length)} />
          <div
            className={clsx(
              'min-h-[60dvh] max-h-[80dvh] overflow-auto divide-y divide-red-700 last:border-0',
              initialLoading && 'py-10',
              !initialLoading && !notificationData?.length && 'pt-32 pb-48'
            )}
          >
            {initialLoading ? (
              <div className="p-4">
                {Array(4)
                  .fill(null)
                  .map((_, i) => (
                    <Skeleton key={i} active avatar />
                  ))}
              </div>
            ) : !notificationData?.length ? (
              <Empty description={capitalize(t('no_notifications'))} />
            ) : (
              <>
                <Collapse
                  bordered={false}
                  expandIconPosition="end"
                  defaultActiveKey={collapseItems.map((item) => item.key)}
                  items={collapseItems}
                  rootClassName="bg-surface!"
                  expandIcon={({ isActive }) => (
                    <RiArrowDownSLine className={clsx('size-5.5', !isActive && '-rotate-90')} />
                  )}
                />
                {hasNextPage && (
                  <div ref={loadNextRef} className="p-4">
                    <Skeleton active paragraph={false} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    >
      {headerBell}
    </Dropdown>
  );
}
